import { DatePickerView } from "@material-ui/pickers";
import { ParsableDate } from "@material-ui/pickers/constants/prop-types";
import React, { JSXElementConstructor } from "react";

import { AutocompleteOptions } from "../../../assets/data/options";
import { Highlights, HighlightsProps } from "../../../components/sale/Highlights";
import i18n from "../../../services/i18n";
import { getFormatter, InputType } from "../../../utils/inputs";
import { getInputDateFormat } from "../../../utils/utility";
import { SubformProps } from "../../SubformProps";
import { Autocomplete, Checkbox, CountryAutocomplete, DatePicker, PhoneField, SelectField, TextField } from "../elements";
import { AutocompleteProps } from "../elements/Autocomplete";
import { CheckboxProps } from "../elements/Checkbox";
import { CountryAutocompleteProps } from "../elements/CountryAutocomplete";
import { DatePickerProps } from "../elements/DatePicker";
import { FileUpload, FileUploadProps } from "../elements/FileUpload";
import { RichTextEditor, RichTextEditorProps } from "../elements/RichTextEditor";
import { TextFieldProps } from "../elements/TextField";

type InputFieldInternalProps = Partial<SubformProps<any>> & {
  id: string;
}

function InputFieldInternal(
  inputType: InputType, props: any, InputComponent: JSXElementConstructor<any>
) {
  const {
    disabled,
    errors,
    id,
    setFieldValue,
    touched,
    values,
  } = props;

  const childProps = { ...props };
  delete childProps.isSubmitting;
  delete childProps.isValidating;
  delete childProps.onClick;
  delete childProps.setFieldValue;
  delete childProps.setValues;

  // Generated props
  const error = touched && touched[id] && errors && errors[id];

  const generatedProps = {
    disabled,
    error,
    id,
    required: !disabled,
    value: values[id],
    checked: values[id],
  }

  // Input type props
  const format = getFormatter(inputType);
  const inputTypeProps = {
    onChange: (e: any) => setFieldValue(
      id,
      format(e),
    )
  };

  return (
    <InputComponent
      { ...inputTypeProps }
      { ...generatedProps }
      { ...childProps }
    />
  );
}

export type TextFieldInternalProps = Partial<TextFieldProps> & InputFieldInternalProps;

export const TextFieldInternal = (props: TextFieldInternalProps) => InputFieldInternal(
  InputType.TextField,
  props,
  TextField
);

export type SelectOption = {
  label: string,
  value: any,
};

export type SelectFieldInternalProps = Partial<TextFieldProps> & InputFieldInternalProps & {
  options: Array<SelectOption>;
  onChange?: (e: any) => void;
}

export const SelectFieldInternal = (props: SelectFieldInternalProps) => InputFieldInternal(
  InputType.TextField,
  props,
  SelectField
);

export type PhoneFieldInternalProps = TextFieldInternalProps

export const PhoneFieldInternal = (props: PhoneFieldInternalProps) => InputFieldInternal(
  InputType.TextField,
  props,
  PhoneField
);

export type DatePickerInternalProps = Partial<DatePickerProps> & InputFieldInternalProps & {
  disableFuture: boolean;
  disablePast: boolean;
  initialFocusedDate: ParsableDate;
  label: string;
  minDate?: ParsableDate;
  maxDate?: ParsableDate;
}

export const DatePickerInternal = (props: DatePickerInternalProps) => {
  const inputDateFormat = getInputDateFormat(i18n.language);
  return InputFieldInternal(
    InputType.Date,
    {
      ...props,
      format: inputDateFormat,
      openTo: 'year' as DatePickerView,
      placeholder: inputDateFormat,
      views: ['year', 'month', 'date'] as Array<DatePickerView>,
    },
    DatePicker
  );
}

DatePickerInternal.defaultProps = {
  disableFuture: false,
  disablePast: false,
  initialFocusedDate: undefined,
}

export type AutocompleteOption = {
  label: string,
  code: any,
};

export type AutocompleteInternalProps = Partial<AutocompleteProps> & InputFieldInternalProps & {
  options: Array<AutocompleteOption>;
}

export const AutocompleteInternal = (props: AutocompleteInternalProps) => InputFieldInternal(
  InputType.Autocomplete,
  props,
  Autocomplete
);

export type CountryAutocompleteInternalProps = Partial<CountryAutocompleteProps> & InputFieldInternalProps;

export const CountryAutocompleteInternal = (props: CountryAutocompleteInternalProps) => InputFieldInternal(
  InputType.Autocomplete,
  {
    ...props,
    options: AutocompleteOptions().countries,
  },
  CountryAutocomplete
);

export type CheckboxInternalProps = Partial<CheckboxProps> & InputFieldInternalProps & {
  label: React.ReactNode;
  onChange?: (e: any) => void;
}

export const CheckboxInternal = (props: CheckboxInternalProps) => InputFieldInternal(
  InputType.Checkbox,
  props,
  Checkbox
);

export type IBANFieldInternalProps = TextFieldInternalProps

export const IBANFieldInternal = (props: IBANFieldInternalProps) => InputFieldInternal(
  InputType.IBAN,
  props,
  SelectField
);

export type IntegerFieldInternalProps = TextFieldInternalProps

export const IntegerFieldInternal = (props: IntegerFieldInternalProps) => InputFieldInternal(
  InputType.Integer,
  {
    ...props,
    type: "number",
  },
  TextField
);

export type FileUploadInternalProps = Partial<FileUploadProps> & InputFieldInternalProps

export const FileUploadInternal = (props: FileUploadInternalProps) => {
  const {
    id,
    setFieldValue,
  } = props;

  return InputFieldInternal(
    InputType.Undefined,
    {
      ...props,
      onFilesSelected: (values: any) => {
        setFieldValue(
          id,
          values,
        );

        if (props.onFilesSelected) {
          props.onFilesSelected(values);
        }
      },
      onUploadEnded: (values: any) => {
        setFieldValue(
          id,
          values,
        );

        if (props.onUploadEnded) {
          props.onUploadEnded(values);
        }
      },
    },
    FileUpload
  );
}

export type HighlightsFieldInternalProps = Partial<HighlightsProps> & InputFieldInternalProps

export const HighlightsFieldInternal = (props: HighlightsFieldInternalProps) => InputFieldInternal(
  InputType.Undefined,
  props,
  Highlights
);

export type RichTextFieldInternalProps = Partial<RichTextEditorProps> & InputFieldInternalProps

export const RichTextFieldInternal = (props: RichTextFieldInternalProps) => InputFieldInternal(
  InputType.Undefined,
  props,
  RichTextEditor
);

export function KeyUndefinedError(id: never): never;

export function KeyUndefinedError(id: any) {
  throw new Error('Unknown key: ' + id);
}